/**
 * Bumped whenever the rasterization of a scene changes — any value in
 * engine/psd-render/parity/fixtures/render-rule/render-rule.json or the drawing itself — or a print file wraps
 * the same pixels differently (its embedded ICC profile), or the person-caption substitution prints another text
 * or size into an unchanged saved model, or print prep hides a box of it (untouched default text): it keys
 * preview-tile and print-archive caches, which must not serve what the previous rule produced. A new `textLayout`
 * step that leaves every box below it pixel for pixel (pinned by the legacy layout snapshots) is not such a change:
 * only boxes stamped with the new step lay out by it, and their model is new — no bump, as with step 6. Mirrors
 * PSD_RENDER_RULE_VERSION in engine/psd-render/core/src/adapter/mod.rs.
 */
export const PSD_RENDER_RULE_VERSION = 72;

/** Cache-key fragment carrying the rule version, so a bump invalidates every derived hash at once. */
export const psdRenderRuleStamp = (version: number): string => `psd-render-rule:${version}`;

export const PSD_RENDER_RULE_STAMP = psdRenderRuleStamp(PSD_RENDER_RULE_VERSION);

/**
 * Ceiling for a DECLARED rule version (HTTP DTOs and the sanitizer below share it). It is not the current
 * constant: a browser bundle newer than the receiving service legally declares a version above
 * PSD_RENDER_RULE_VERSION during a rollout, so the ceiling must sit far beyond any reachable version, not
 * one step ahead. The constant is a hand-bumped literal (here and as a u32 in the Rust adapter) that moves
 * once per rule change, so a million is unreachable, while 1e21, 2^53 and anything else past the
 * safe-integer range fall outside it.
 */
export const PSD_RENDER_RULE_VERSION_MAX = 1_000_000;

/**
 * Which rule drew the pixels is the PRODUCER's word, never the persister's. Two producers draw spread
 * previews: the Rust adapter behind the signed render callback (it declares its own constant in the
 * callback body — the service receiving the callback is a separate deployment and runs minutes apart
 * from the renderer on a rollout) and the editor's own canvas in the user's browser (which declares the
 * constant of the bundle that tab loaded — hours older than a rule bump while a photographer keeps the
 * editor open). A writer stamping its own constant over either would pin pre-bump pixels as current
 * and hide them from the sweep that exists to replace them.
 *
 * Anything but an integer in [0, PSD_RENDER_RULE_VERSION_MAX] writes NO key at all — absence already reads
 * as «behind», so an unknown producer (a renderer from before the field existed included) is re-rendered
 * rather than trusted.
 */
export const declaredRenderRuleVersion = (declared: unknown): { renderRuleVersion?: number } =>
  typeof declared === 'number' && Number.isInteger(declared) && declared >= 0 && declared <= PSD_RENDER_RULE_VERSION_MAX
    ? { renderRuleVersion: declared }
    : {};

/** The rule a stamp names, through the same ceiling as a declared one; a foreign or empty stamp names none. */
export const renderRuleOfStamp = (stamp: string | null | undefined): number | undefined => {
  const match = /^psd-render-rule:(\d+)$/.exec(stamp ?? '');
  return match ? declaredRenderRuleVersion(Number(match[1])).renderRuleVersion : undefined;
};
